// ============================================
// SECCIONALMACENPRODUCTO.JS — La cara de almacen dentro de la pagina de producto de La Tienda (2026-10-19)
// ============================================
// El buscador de La Tienda trae AQUI (misma ficha). Para quien tiene permiso de Almacen ensena, de este producto:
//   - EXISTENCIA fisica · DISPONIBLE · APARTADO (nunca solo la existencia);
//   - los LOTES de que compra viene cada pieza (CMP, fecha, costo real si ve costos, garantia del proveedor, quedan N,
//     en orden FIFO): la trazabilidad "¿a que pedido corresponde?";
//   - "Apartado para" que expedientes, y las series ya instaladas (amarradas a su sucursal);
//   - APARTAR (reservar) y SURTIR (entregar) para una linea de un expediente que pida este producto.
// Si el servidor contesta 403 (sin permiso de Almacen) la seccion no se pinta.
import React, { useCallback, useEffect, useState } from 'react';
import { fetchAuth } from '../../utils/sesion';
import { API_BASE } from '../../config/api';
import { fmtDinero } from '../../utils/dinero';
// SUB-ALMACENES (2026-10-19): donde esta cada pieza (Central, Oficina, tecnicos por expediente, instalado) y el kardex.
import Kardex from './Kardex';

const PP = "'Poppins', sans-serif";
const ICONO_SUB = { central: '📦', oficina: '🏢', tecnico: '🪛', instalado: '📷' };
const C = { oscuro: '#2f5f9e', gris: '#4e5357', grisClaro: '#8a8f95', rojo: '#e05c5c', verde: '#1D9E75', ambar: '#b45309', borde: '#e5e8ec', principal: '#5c9adb' };
const fmtCant = (n) => Number(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 4 });
const fmtDia = (v) => { if (!v) return '—'; try { return new Date(String(v).slice(0, 10) + 'T12:00:00').toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; } };
const btn = (color = C.grisClaro, fondo = '#fff', texto) => ({ padding: '6px 10px', minHeight: 36, borderRadius: 8, background: fondo, color: texto || color, border: `1px solid ${color}`, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: PP });
const inputMini = { width: 90, minHeight: 36, padding: '4px 8px', borderRadius: 8, border: '1px solid #d8dde3', fontFamily: PP, fontSize: 15, boxSizing: 'border-box' };

export default function SeccionAlmacenProducto({ idUnico, isMobile }) {
  const [d, setD] = useState(null);
  const [oculta, setOculta] = useState(false);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [apartarPara, setApartarPara] = useState(null); // { item, cantidad }
  const cargar = useCallback(async () => {
    try {
      const r = await fetchAuth(`${API_BASE}/api/inventario/ficha/${encodeURIComponent(idUnico)}`);
      if (r.status === 403) { setOculta(true); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || 'No se pudo leer el almacén');
      setD(j); setError('');
    } catch (e) { if (e.code !== 'SESION_EXPIRADA') setError(e.message); }
  }, [idUnico]);
  useEffect(() => { cargar(); }, [cargar]);

  const accion = async (item, ruta, body, okMsg) => {
    if (ocupado) return;
    setOcupado(true); setError(''); setAviso('');
    try {
      const r = await fetchAuth(`${API_BASE}/api/solicitudes-material/${item.solicitud_id}/items/${item.solicitud_item_id}/${ruta}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || 'No se pudo completar');
      setAviso(okMsg); setApartarPara(null);
      await cargar();
    } catch (e) { if (e.code !== 'SESION_EXPIRADA') setError(e.message); }
    finally { setOcupado(false); }
  };

  if (oculta) return null;
  if (!d && !error) return null;
  const ex = d && d.existencia;
  const existencia = Number(ex ? ex.cantidad : 0), apartado = Number(ex ? ex.apartado : 0), disponible = Number(ex ? ex.disponible : 0);
  const lotes = (d && d.lotes) || [], apartados = (d && d.apartados) || [], series = (d && d.series) || [], pendientes = (d && d.pendientes) || [];
  const subalmacenes = (d && d.subalmacenes) || [], kardex = (d && d.kardex) || [];
  const veCostos = !!(d && d.ve_costos);
  const unidad = ex && ex.unidad === 'metro' ? 'm' : 'pza';

  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: isMobile ? 14 : 20, border: `1px solid ${C.borde}`, marginBottom: 24, fontFamily: PP }}>
      <div style={{ fontSize: 15, fontWeight: 800, color: C.oscuro, marginBottom: 10 }}>📦 En el almacén</div>
      {error && <div style={{ background: '#fde8e8', color: '#b02020', borderRadius: 10, padding: '8px 10px', fontSize: 12, marginBottom: 8 }}>{error}</div>}
      {aviso && <div style={{ background: '#e8faf5', color: C.verde, borderRadius: 10, padding: '8px 10px', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>{aviso}</div>}

      {/* Existencia · Disponible · Apartado: nunca solo existencia */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
        <Dato etiqueta="Existencia física" valor={`${fmtCant(existencia)} ${unidad}`} color={existencia > 0 ? C.oscuro : C.rojo} />
        <Dato etiqueta="Disponible" valor={`${fmtCant(disponible)} ${unidad}`} color={disponible > 0 ? C.verde : C.rojo} sub="existencia − apartado" />
        <Dato etiqueta="Apartado" valor={`${fmtCant(apartado)} ${unidad}`} color={apartado > 0 ? C.ambar : C.gris} sub={apartados.length ? `${apartados.length} expediente${apartados.length === 1 ? '' : 's'}` : 'para nadie'} />
      </div>
      {veCostos && ex && ex.costo_promedio != null && <div style={{ fontSize: 12, color: C.grisClaro, marginTop: 6 }}>Costo promedio del almacén <strong style={{ color: C.gris }}>{fmtDinero(ex.costo_promedio)}</strong> · valor <strong style={{ color: C.gris }}>{fmtDinero(ex.costo_total)}</strong></div>}
      {!ex && <div style={{ fontSize: 12, color: C.grisClaro, marginTop: 6 }}>Este producto no tiene existencia. Entra al almacén cuando se confirma una compra en Almacén › Compras.</div>}

      {/* Sub-almacenes: donde esta cada pieza (la existencia de arriba es CENTRAL) */}
      {ex && (existencia > 0 || subalmacenes.length > 0) && (
        <div style={{ marginTop: 10, fontSize: 12, color: C.gris, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontWeight: 700, color: C.grisClaro, textTransform: 'uppercase', fontSize: 10.5 }}>Dónde está</span>
          <span>📦 Central <strong>{fmtCant(existencia)}</strong></span>
          {subalmacenes.map(s => (
            <span key={s.subalmacen_id} title={(s.detalle || []).map(x => `${x.expediente_folio || 'sin expediente'}: ${fmtCant(x.cantidad)}`).join(' · ')}>
              {ICONO_SUB[s.tipo] || '↗'} {s.nombre} <strong>{fmtCant(s.cantidad)}</strong>
              {s.tipo === 'tecnico' && (s.detalle || []).length > 0 ? <span style={{ color: C.grisClaro }}> ({s.detalle.map(x => x.expediente_folio || 'sin expediente').join(', ')})</span> : null}
            </span>
          ))}
        </div>
      )}

      {/* Lotes: trazabilidad hasta la compra, FIFO */}
      {lotes.length > 0 && (
        <>
          <div style={{ fontSize: 11, color: C.grisClaro, fontWeight: 700, textTransform: 'uppercase', marginTop: 14, marginBottom: 6 }}>Lotes · de qué compra viene (el más antiguo sale primero)</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {lotes.map((l, i) => {
              const agotado = Number(l.cantidad_restante) <= 0;
              return (
                <div key={l.id} style={{ border: `1px solid ${C.borde}`, borderRadius: 10, padding: '8px 10px', fontSize: 12, opacity: agotado ? 0.55 : 1, display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'space-between' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: C.gris }}>{!agotado && i === lotes.findIndex(x => Number(x.cantidad_restante) > 0) ? '① ' : ''}{l.compra_folio ? `${l.compra_folio} · ${l.proveedor || ''}` : (l.origen === 'inicial' ? 'Existencia previa (sin compra)' : 'Entrada a mano')}{l.fecha_compra ? ` · ${fmtDia(l.fecha_compra)}` : ''}</div>
                    <div style={{ color: C.grisClaro, marginTop: 2 }}>
                      {veCostos && l.costo_unitario != null ? <>costo real <strong style={{ color: C.gris }}>{fmtDinero(l.costo_unitario)}</strong> · </> : null}
                      {l.garantia_vence
                        ? <span style={{ color: l.garantia_vigente ? C.verde : C.rojo, fontWeight: 700 }}>🛡️ garantía del proveedor hasta {fmtDia(l.garantia_vence)} ({l.garantia_vigente ? `${l.garantia_dias} días` : 'vencida'})</span>
                        : <span>🛡️ garantía del proveedor por confirmar</span>}
                    </div>
                  </div>
                  <div style={{ fontWeight: 800, color: agotado ? C.grisClaro : C.oscuro, flexShrink: 0 }}>quedan {fmtCant(l.cantidad_restante)} / {fmtCant(l.cantidad_inicial)}</div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Apartado para */}
      {apartados.length > 0 && (
        <>
          <div style={{ fontSize: 11, color: C.grisClaro, fontWeight: 700, textTransform: 'uppercase', marginTop: 14, marginBottom: 6 }}>Apartado para</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {apartados.map(a => (
              <div key={a.id} style={{ fontSize: 12, color: C.gris, display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <span><strong>{a.expediente_folio || a.solicitud_folio}</strong>{a.cliente_nombre ? ` · ${a.cliente_nombre}` : ''}{a.sucursal_nombre ? ` · ${a.sucursal_nombre}` : ''} <span style={{ color: C.grisClaro }}>· desde {fmtDia(a.creado_en)}</span></span>
                <strong style={{ color: C.ambar }}>{fmtCant(a.cantidad)} {unidad}</strong>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Expedientes que lo piden: apartar / surtir desde aqui */}
      {pendientes.length > 0 && (
        <>
          <div style={{ fontSize: 11, color: C.grisClaro, fontWeight: 700, textTransform: 'uppercase', marginTop: 14, marginBottom: 6 }}>Lo piden estos expedientes · apartar y surtir</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {pendientes.map(p => {
              const pend = Number(p.cantidad_pendiente_surtir), ap = Number(p.apartado);
              const topeApartar = Math.min(pend, disponible + ap);
              const topeSurtir = Math.min(pend, disponible + ap);
              const editando = apartarPara && apartarPara.item.solicitud_item_id === p.solicitud_item_id;
              return (
                <div key={p.solicitud_item_id} style={{ border: `1px solid ${C.borde}`, borderRadius: 10, padding: '8px 10px', fontSize: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ color: C.gris }}><strong>{p.expediente_folio || p.solicitud_folio}</strong>{p.cliente_nombre ? ` · ${p.cliente_nombre}` : ''}{p.sucursal_nombre ? ` · ${p.sucursal_nombre}` : ''} <span style={{ color: C.grisClaro }}>· {p.estado_linea === 'falta' ? 'en falta' : 'por revisar'}</span></span>
                    <span style={{ color: C.oscuro, fontWeight: 700 }}>faltan {fmtCant(pend)}{ap > 0 ? <span style={{ color: C.ambar }}> · {fmtCant(ap)} apartadas</span> : null}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    {!editando && topeApartar > 0 && <button disabled={ocupado} onClick={() => setApartarPara({ item: p, cantidad: String(topeApartar) })} style={btn(C.ambar)}>🔖 Apartar</button>}
                    {!editando && ap > 0 && <button disabled={ocupado} onClick={() => accion(p, 'desapartar', {}, `Se liberó lo apartado para ${p.expediente_folio || p.solicitud_folio}.`)} style={btn(C.grisClaro)}>Quitar apartado</button>}
                    {!editando && topeSurtir > 0 && <button disabled={ocupado} onClick={() => accion(p, 'traer', topeSurtir >= pend ? {} : { cantidad: String(topeSurtir) }, `Surtidas ${fmtCant(topeSurtir)} a ${p.expediente_folio || p.solicitud_folio}: salieron del almacén (FIFO) y el costo real cayó al proyecto.`)} style={btn(C.verde, C.verde, '#fff')}>📤 Surtir {topeSurtir >= pend ? 'todo' : fmtCant(topeSurtir)}</button>}
                    {topeApartar <= 0 && ap <= 0 && <span style={{ color: C.rojo, fontWeight: 700 }}>sin disponibles para este expediente</span>}
                    {editando && (
                      <>
                        <input inputMode="decimal" value={apartarPara.cantidad} onChange={(e) => setApartarPara({ ...apartarPara, cantidad: e.target.value })} style={inputMini} autoFocus />
                        <button disabled={ocupado || !/^\d+(\.\d+)?$/.test(apartarPara.cantidad.trim()) || Number(apartarPara.cantidad) <= 0 || Number(apartarPara.cantidad) > topeApartar} onClick={() => accion(p, 'apartar', { cantidad: apartarPara.cantidad.trim() }, `Apartadas ${fmtCant(apartarPara.cantidad)} para ${p.expediente_folio || p.solicitud_folio}: dejan de estar disponibles para otros.`)} style={btn(C.ambar, C.ambar, '#fff')}>Apartar</button>
                        <button onClick={() => setApartarPara(null)} style={btn()}>Cancelar</button>
                        <span style={{ color: C.grisClaro }}>máx {fmtCant(topeApartar)}</span>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Series instaladas */}
      {series.length > 0 && (
        <>
          <div style={{ fontSize: 11, color: C.grisClaro, fontWeight: 700, textTransform: 'uppercase', marginTop: 14, marginBottom: 6 }}>Instalados con serie ({series.length}) · amarrados a su sucursal 🔒</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {series.map(s => (
              <div key={s.id} style={{ fontSize: 12, color: C.gris, display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <span><strong style={{ fontFamily: 'monospace' }}>{s.serie}</strong>{s.posicion ? ` · ${s.posicion}` : ''} <span style={{ color: C.grisClaro }}>· {s.expediente_folio || ''}{s.sucursal_nombre ? ` · ${s.sucursal_nombre}` : ''}{s.cliente_nombre ? ` · ${s.cliente_nombre}` : ''}</span></span>
                <span style={{ color: C.grisClaro }}>{fmtDia(s.instalado_en)}{s.compra_folio ? ` · ${s.compra_folio}` : ''}{s.garantia_vence ? ` · garantía ${fmtDia(s.garantia_vence)}` : ''}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Kardex: cada traspaso de este producto */}
      {kardex.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <Kardex filas={kardex} titulo="Kardex · de dónde a dónde" compacto />
        </div>
      )}
    </div>
  );
}

function Dato({ etiqueta, valor, sub, color }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 10.5, color: C.grisClaro, fontWeight: 700, textTransform: 'uppercase' }}>{etiqueta}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: color || C.oscuro, lineHeight: 1.1 }}>{valor}</div>
      {sub && <div style={{ fontSize: 10.5, color: C.grisClaro }}>{sub}</div>}
    </div>
  );
}
